import { store } from 'redux/store';
import { State } from 'redux/initialState';
import { useHistory, useParams } from 'react-router-dom';
import { useTranslate } from 'hooks/useTranslate';
import { useSelector } from 'react-redux';
import { FC, ReactNode, createContext, useContext, useState } from 'react';

import actionTypes from 'constants/actionTypes';
import sizes from 'constants/sizes';

import { setModal } from 'modules/_shared/redux/modalActions';
import { HoldingClass, Operation, Partner } from 'types';
import { useGetActualSociety } from 'hooks/useGetActualSociety';
import { useGetMenu } from 'modules/administratives/hooks/useGetMenu';
import { useGetShare } from 'modules/partners/hooks/useGetShare';
import { useGetPartnersSectionPermissions } from 'modules/partners/hooks/useGetPartnerSectionsPermissions';
import { useGetEncumbrances } from 'modules/partners/hooks/useGetEncumbrances';
import { EditShareModalProvider } from 'modules/partners/modals/EditShareModal/context/EditShareModalContext';
import { AddEncumbranceModalProvider } from 'modules/partners/modals/AddEncumbranceModal/context/AddEncumbranceModalContext';
import AddEncumbranceModal from 'modules/partners/modals/AddEncumbranceModal';
import EditShareModal from 'modules/partners/modals/EditShareModal';
import { deleteEncumbrance } from 'modules/partners/redux/encumbranceActions';

import errorSwal from 'utils/errorSwal';
import successSwal from 'utils/successSwal';
import asyncActionSwal from 'utils/asyncActionSwal';

import {
  EncumbranceOption,
  encumbranceOptions,
} from '../../../constants/encumbranceOptions';
import { shareDetailPageInitValue } from '../constants/shareDetailsPageInitValue';
import { ShareDetailsPageContextType } from '../types/ShareDetailsPageContextType';

const ShareDetailsPageContext = createContext<ShareDetailsPageContextType>(
  shareDetailPageInitValue
);

export const ShareDetailsPageProvider: FC<{
  children: ReactNode;
}> = ({ children }) => {
  const { t } = useTranslate();
  const { shareId, partnerId } = useParams();
  const history = useHistory();

  const actualSociety = useSelector(
    (state: State) => state.society?.actualSociety
  );

  const encumbrances = useSelector((state: State) => state.encumbrances);

  useGetActualSociety(actualSociety?._id);
  useGetMenu();

  const [page, setPage] = useState<number>(1);
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());
  const [refetchKey, setRefetchKey] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const { data: shareData, isLoading: isLoadingShare } = useGetShare({
    shareId,
    fieldsToPopulate: ['partner', 'shareClass', 'operation'],
    refetchKey,
  });

  const { isLoading: isLoadingEncumbrances } = useGetEncumbrances({
    shareId,
    page,
    rowsPerPage,
  });

  const { data: permissionsData } = useGetPartnersSectionPermissions();

  const invalidateShare = () => {
    setRefetchKey((v) => v + 1);
  };

  const handleChangePage = (newPage: number) => {
    setPage(newPage);
  };
  const handleChangeRowsPerPage = (newRowsPerPage: number) => {
    setRowsPerPage(newRowsPerPage);
    setPage(1);
  };

  const handleEditShare = () => {
    if (!shareData?.share) return;

    store.dispatch(
      setModal(
        <EditShareModalProvider
          share={shareData?.share}
          onSave={invalidateShare}
        >
          <EditShareModal />
        </EditShareModalProvider>
      )
    );
  };

  const handleViewEncumbrance = (index: number) => {
    const encumbrance = encumbrances[index];
    if (!encumbrance || !shareData?.share) return;

    history.push(
      `/detalle-carga/${actualSociety?._id}/${partnerId}/${shareId}/${encumbrance?._id}`
    );
  };

  const handleAddEncumbrance = () => {
    if (!shareData?.share) return;

    store.dispatch(
      setModal(
        <AddEncumbranceModalProvider
          share={shareData?.share}
          modalActionType={actionTypes.ADD}
          size={sizes.XL}
          onAddEncumbranceSuccess={invalidateShare}
        >
          <AddEncumbranceModal />
        </AddEncumbranceModalProvider>
      )
    );
  };

  const handleEditEncumbrance = (index: number) => {
    const encumbrance = encumbrances[index];
    if (!encumbrance || !shareData?.share) return;

    store.dispatch(
      setModal(
        <AddEncumbranceModalProvider
          share={shareData?.share}
          encumbrance={encumbrance}
          modalActionType={actionTypes.EDIT}
          size={sizes.XL}
        >
          <AddEncumbranceModal />
        </AddEncumbranceModalProvider>
      )
    );
  };

  const handleToggleRowExpansion = (index: number) => {
    setExpandedRows((prevExpandedRows) => {
      const newExpandedRows = new Set(prevExpandedRows);
      if (newExpandedRows.has(index)) {
        newExpandedRows.delete(index);
      } else {
        newExpandedRows.add(index);
      }
      return newExpandedRows;
    });
  };

  const handleRemoveEncumbrance = (index: number) => {
    const encumbrance = encumbrances[index];

    const selectedEncumbrance = encumbranceOptions.find(
      (option) => option.value === encumbrance?.type
    ) as EncumbranceOption | undefined;

    const encumbranceLabel = selectedEncumbrance
      ? selectedEncumbrance.label
      : '';

    asyncActionSwal({
      swalTitle: t('AreYouSureAboutDelEncumbrance'),
      swalElementName: t(`ShareEncumbranceOption_${encumbranceLabel}`),
      swalInfoText: t('ThisProcedureCantUndo'),
      swalConfirmButtonText: t('OkDelete'),
      callback: async () => {
        try {
          if (!encumbrance?._id) return;
          setIsLoading(true);

          await store.dispatch(
            deleteEncumbrance({
              encumbranceId: encumbrance._id,
            })
          );

          invalidateShare();
          successSwal(t('EncumbranceDeletedSuccessfully'));
        } catch (error) {
          errorSwal(t('ErrorDeletingEncumbrance'));
        } finally {
          setIsLoading(false);
        }
      },
    });
  };

  return (
    <ShareDetailsPageContext.Provider
      value={{
        data: {
          from: shareData?.share?.from || 0,
          to: shareData?.share?.to || 0,
          price: shareData?.share?.sharePrice || 0,
          nominalValue: shareData?.share?.nominalValue || 0,
          premium: shareData?.share?.sharePremium || 0,
          tag: shareData?.share?.tag || '',
          partner: (shareData?.share?.partner as Partner) || null,
          holdingClass: (shareData?.share?.shareClass as HoldingClass) || null,
          operation: (shareData?.share?.operation as Operation) || null,
          encumbrances: encumbrances || [],
          encumbrancesTotal: shareData?.share?.encumbrancesCount?.total || 0,
          expandedRows,
          page,
          rowsPerPage,
          isAdmin: permissionsData?.isAdmin || false,
          isReadOnly: permissionsData?.isReadOnly || false,
          isLoading: isLoadingShare || isLoadingEncumbrances || isLoading,
        },
        actions: {
          handleChangePage,
          handleChangeRowsPerPage,
          handleEditShare,
          handleToggleRowExpansion,
          handleAddEncumbrance,
          handleEditEncumbrance,
          handleViewEncumbrance,
          handleRemoveEncumbrance,
        },
      }}
    >
      {children}
    </ShareDetailsPageContext.Provider>
  );
};

export const useShareDetailsPage = () => {
  const context = useContext(ShareDetailsPageContext);
  if (!context) {
    throw new Error(
      'useShareDetailsPage must be used within a ShareDetailsPageProvider'
    );
  }
  return context;
};
